import { HTMLAttributes } from 'react'
import Link from 'next/link'

import Image from '@/components/base/lazy-image'
import Typography from '@/components/base/typography'

import SVGIcon from './svg-icon'
import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

export type BorderedCardProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    title: string
    link: string
    image: WPImage | string
    variant: 'standard' | 'compact'
    fill: boolean
  }>

const BorderedCard = ({
  link,
  title,
  image,
  fill = false,
  variant = 'standard',
  className,
  ...props
}: BorderedCardProps) => {
  return (
    <div className={cn('group/card relative grid gap-y-[2rem]', className)} {...props}>
      <div
        className={cn(
          'relative overflow-hidden rounded-[0.3rem] bg-light',
          'after:absolute after:left-0 after:top-0 after:size-full after:border-[0rem] after:border-[#EBECEA]',
          'after:transition-all after:duration-300 after:ease-out-cubic group-hover/card:after:border-[3rem]',
          {
            'after:opacity-40 after:mix-blend-overlay': fill,
            'bg-light': !fill
          },
          'group-has-[a:focus-visible]/card:ring-2 group-has-[a:focus-visible]/card:ring-[var(--focus-ring-dark)]'
        )}
      >
        <div
          className={cn(
            'relative aspect-[243/300] w-full lg:aspect-[287/380]',
            'flex items-center justify-center'
          )}
        >
          {image ? (
            <Image
              src={
                typeof image === 'string' ? image : image?.sizes?.medium?.url || image?.url || ''
              }
              alt=""
              fill
              className={cn('object-contain', {
                'object-cover': fill,
                'p-[20%] mix-blend-multiply': !fill
              })}
            />
          ) : (
            <SVGIcon symbol="logo" className="h-[2.6rem] w-[13rem] text-dark" />
          )}
        </div>
      </div>

      {link ? (
        <Link
          href={link}
          scroll={false}
          className={cn(
            'flex items-center outline-none',
            'after:absolute after:left-0 after:top-0 after:size-full'
          )}
        >
          <span className="relative size-full">
            <span className="absolute left-0 top-1/2 -translate-y-1/2 overflow-hidden">
              <SVGIcon
                symbol="arrow-right"
                className={cn(
                  'size-[2rem] -translate-x-full opacity-0',
                  'transition-all duration-500 ease-out-cubic',
                  'group-hover/card:translate-x-0 group-hover/card:opacity-100',
                  'group-has-[a:focus-visible]/card:translate-x-0 group-has-[a:focus-visible]/card:opacity-100'
                )}
              />
            </span>

            <span
              className={cn(
                'flex size-full',
                'transition-transform duration-500 ease-out-cubic'
                // 'group-hover/card:translate-x-1/2'
              )}
            >
              <span
                className={cn(
                  'inline-flex pr-[4rem]',
                  'transition-transform duration-500 ease-out-cubic',
                  'group-hover/card:translate-x-[4rem]',
                  'group-has-[a:focus-visible]/card:translate-x-[4rem]'
                )}
              >
                <Typography
                  variant={variant === 'compact' ? 'history-size-body' : 'standard'}
                  className=""
                >
                  {title}
                </Typography>
              </span>
            </span>
          </span>
        </Link>
      ) : (
        <Typography variant={variant === 'compact' ? 'history-size-body' : 'standard'} className="">
          {title}
        </Typography>
      )}
    </div>
  )
}

export default BorderedCard
